import React, { useState, useEffect } from 'react';
import { authAPI, resourcesAPI, contactsAPI, calendarAPI, formsAPI } from './api';
import { VideoPlayer } from './VideoPlayer';
import { AdvancedSearch } from './AdvancedSearch';

// Modern Icon Components
const ScaleIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
  </svg>
);

const DocumentIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const CalendarIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const BookIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
  </svg>
);

const TrendingUpIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
  </svg>
);

const PhoneIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
  </svg>
);

const AcademicCapIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
  </svg>
);

const HomeIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
  </svg>
);

const HeartIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
  </svg>
);

const CurrencyDollarIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
  </svg>
);

const ShieldCheckIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
  </svg>
);

const UsersIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
  </svg>
);

const FolderIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
  </svg>
);

const ExclamationIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
  </svg>
);

const ClockIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const BellIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
  </svg>
);

const CheckCircleIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const PlayIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M8 5v14l11-7z"/>
  </svg>
);

const BuildingOfficeIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
  </svg>
);

const HandshakeIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
  </svg>
);

const BadgeCheckIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
  </svg>
);

const PresentationChartLineIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
  </svg>
);

const VideoIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
  </svg>
);

const NewspaperIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
  </svg>
);

const ClipboardListIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
  </svg>
);

// Header Component
export const Header = ({ currentView, setCurrentView, user, onLogout }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const navigationItems = [
    { id: 'dashboard', label: 'Dashboard', icon: HomeIcon, requiresAuth: true },
    { id: 'home', label: 'Family Law Topics', icon: ScaleIcon },
    { id: 'forms', label: 'Family Forms', icon: DocumentIcon },
    { id: 'calendar', label: 'Calendar', icon: CalendarIcon, requiresAuth: true },
    { id: 'resources', label: 'Resources', icon: BookIcon },
    { id: 'appeals', label: 'Appeals', icon: TrendingUpIcon },
    { id: 'directory', label: 'Directory', icon: PhoneIcon },
    { id: 'court-guide', label: 'Court Guide', icon: AcademicCapIcon }
  ];

  return (
    <header className="bg-blue-900 text-white shadow-lg">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between py-4">
          <div className="flex items-center space-x-4">
            <button 
              onClick={() => setCurrentView('home')}
              className="flex items-center space-x-2 hover:opacity-80 transition-opacity"
            >
              <div className="w-10 h-10 bg-orange-500 rounded flex items-center justify-center font-bold text-lg">
                GPJ
              </div>
              <div className="hidden sm:block text-left">
                <h1 className="text-xl font-bold">Good Parents Justice</h1>
                <p className="text-sm text-blue-200">Step-by-step family law help</p>
              </div>
            </button>
          </div>

          <nav className="hidden lg:flex items-center space-x-6">
            {navigationItems
              .filter(item => !item.requiresAuth || user)
              .map((item) => {
                const IconComponent = item.icon;
                return (
                  <button
                    key={item.id}
                    onClick={() => setCurrentView(item.id)}
                    className={`flex items-center space-x-1 px-3 py-2 rounded transition-colors ${
                      currentView === item.id 
                        ? 'bg-blue-800 text-orange-400' 
                        : 'hover:bg-blue-800 hover:text-orange-400'
                    }`}
                  >
                    <IconComponent className="w-4 h-4" />
                    <span className="text-sm font-medium">{item.label}</span>
                  </button>
                );
              })}
          </nav>

          <div className="flex items-center space-x-4">
            {user ? (
              <div className="hidden sm:flex items-center space-x-4">
                <span className="text-sm">Welcome, {user.first_name}!</span>
                <button
                  onClick={onLogout}
                  className="bg-orange-500 text-white px-4 py-2 rounded hover:bg-orange-600 transition-colors text-sm"
                >
                  Logout
                </button>
              </div>
            ) : (
              <div className="hidden sm:flex items-center space-x-2">
                <button
                  onClick={() => setCurrentView('login')}
                  className="text-blue-200 hover:text-white transition-colors text-sm"
                >
                  Login
                </button>
                <button
                  onClick={() => setCurrentView('register')}
                  className="bg-orange-500 text-white px-4 py-2 rounded hover:bg-orange-600 transition-colors text-sm"
                >
                  Register
                </button>
              </div>
            )}

            <button
              type="button"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2 rounded hover:bg-blue-800 transition-colors"
              aria-label="Toggle navigation menu"
              aria-expanded={isMenuOpen}
              aria-controls="legacy-mobile-navigation"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>

        {isMenuOpen && (
          <div id="legacy-mobile-navigation" className="lg:hidden py-4 border-t border-blue-800">
            <nav className="space-y-2">
              {navigationItems
                .filter(item => !item.requiresAuth || user)
                .map((item) => (
                <button
                  key={item.id}
                  onClick={() => {
                    setCurrentView(item.id);
                    setIsMenuOpen(false);
                  }}
                  className={`flex items-center space-x-2 w-full px-3 py-2 rounded text-left transition-colors ${
                    currentView === item.id 
                      ? 'bg-blue-800 text-orange-400' 
                      : 'hover:bg-blue-800 hover:text-orange-400'
                  }`}
                >
                  {(() => {
                    const IconComponent = item.icon;
                    return <IconComponent className="w-4 h-4" />;
                  })()}
                  <span>{item.label}</span>
                </button>
              ))}
            </nav>
            <div className="sm:hidden flex gap-3 px-3 pt-4 mt-4 border-t border-blue-800">
              {user ? (
                <>
                  <span className="text-sm self-center">Welcome, {user.first_name}!</span>
                  <button type="button" onClick={onLogout} className="bg-orange-500 text-white px-4 py-2 rounded text-sm">
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <button type="button" onClick={() => setCurrentView('login')} className="text-blue-200 text-sm">
                    Login
                  </button>
                  <button type="button" onClick={() => setCurrentView('register')} className="bg-orange-500 text-white px-4 py-2 rounded text-sm">
                    Register
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

// Login Component
export const Login = ({ onLogin, setCurrentView }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await authAPI.login(formData);
      localStorage.setItem('access_token', response.data.access_token);
      onLogin(response.data.user);
    } catch (error) {
      setError(error.response?.data?.detail || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="flex justify-center">
            <div className="w-16 h-16 bg-blue-900 rounded-full flex items-center justify-center">
              <span className="text-2xl font-bold text-white">GPJ</span>
            </div>
          </div>
          <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">
            Sign in to your account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Access your family law resources and forms
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                value={formData.password}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-900 hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </div>

          <div className="text-center">
            <button
              type="button"
              onClick={() => setCurrentView('register')}
              className="text-blue-600 hover:text-blue-500 text-sm"
            >
              Don't have an account? Sign up
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Register Component
export const Register = ({ onRegister, setCurrentView }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    phone: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      const registerData = { ...formData };
      delete registerData.confirmPassword;
      
      const response = await authAPI.register(registerData);
      
      // Auto-login after registration
      const loginResponse = await authAPI.login({
        email: formData.email,
        password: formData.password
      });
      
      localStorage.setItem('access_token', loginResponse.data.access_token);
      onRegister(loginResponse.data.user);
    } catch (error) {
      setError(error.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="flex justify-center">
            <div className="w-16 h-16 bg-blue-900 rounded-full flex items-center justify-center">
              <span className="text-2xl font-bold text-white">GPJ</span>
            </div>
          </div>
          <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">
            Create your account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Join Good Parents Justice to access personalized family law help
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="first_name" className="block text-sm font-medium text-gray-700">
                  First Name
                </label>
                <input
                  id="first_name"
                  name="first_name"
                  type="text"
                  required
                  value={formData.first_name}
                  onChange={handleChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label htmlFor="last_name" className="block text-sm font-medium text-gray-700">
                  Last Name
                </label>
                <input
                  id="last_name"
                  name="last_name"
                  type="text"
                  required
                  value={formData.last_name}
                  onChange={handleChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                Phone Number (Optional)
              </label>
              <input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                value={formData.password}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                required
                value={formData.confirmPassword}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-900 hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {loading ? 'Creating account...' : 'Create account'}
            </button>
          </div>

          <div className="text-center">
            <button
              type="button"
              onClick={() => setCurrentView('login')}
              className="text-blue-600 hover:text-blue-500 text-sm"
            >
              Already have an account? Sign in
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
export const Hero = () => {
  const [searchTerm, setSearchTerm] = useState('');

  return (
    <section className="bg-blue-900 text-white relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-r from-blue-900 to-blue-800 opacity-90"></div>
      <div className="container mx-auto px-4 py-16 lg:py-24 relative z-10">
        <div>
          <div className="max-w-3xl mx-auto">
            <h1 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight">
              <span className="text-orange-400">Good Parents Justice</span>
            </h1>
            <h2 className="text-2xl lg:text-3xl font-semibold mb-4">
              Step-by-step family law help for Ontario parents.
            </h2>
            <p className="text-lg lg:text-xl text-blue-100 mb-8">
              Reliable, practical, and easy to understand guidance when you can't afford a lawyer and legal aid isn't available.
            </p>
            
            <div className="relative">
              <input
                type="text"
                placeholder="Tell us what you are looking for..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-6 py-4 rounded-full text-gray-800 text-lg border-none outline-none shadow-lg"
              />
              <button className="absolute right-2 top-2 bg-orange-500 hover:bg-orange-600 text-white p-2 rounded-full transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Announcements Component
export const Announcements = () => {
  const announcements = [
    "New information on child support calculation changes effective January 2024",
    "Updated family court forms now available with AI-assisted completion",
    "New appeals process guide for self-represented parents",
    "Free virtual court preparation workshops every Tuesday"
  ];

  return (
    <section className="bg-blue-800 text-white py-4">
      <div className="container mx-auto px-4">
        <div className="flex items-center space-x-4 overflow-x-auto">
          <span className="text-orange-400 font-semibold whitespace-nowrap">Announcements</span>
          <div className="flex space-x-8">
            {announcements.map((announcement, index) => (
              <span key={index} className="whitespace-nowrap text-sm">
                {announcement}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Legal Topics Component
export const LegalTopics = ({ setCurrentView }) => {
  const topics = [
    {
      id: 'divorce',
      title: 'Divorce & Separation',
      icon: HeartIcon,
      description: 'Separation and divorce procedures, child support calculations',
      items: ['Divorce process', 'Separation agreements', 'Property division']
    },
    {
      id: 'custody',
      title: 'Child Custody & Access',
      icon: UsersIcon,
      description: 'Custody arrangements and parenting time',
      items: ['Custody types', 'Parenting plans', 'Access schedules']
    },
    {
      id: 'support',
      title: 'Child & Spousal Support',
      icon: CurrencyDollarIcon,
      description: 'Support calculations and enforcement',
      items: ['Child support guidelines', 'Spousal support', 'Enforcement']
    },
    {
      id: 'violence',
      title: 'Domestic Violence',
      icon: ShieldCheckIcon,
      description: 'Protection orders and safety planning',
      items: ['Restraining orders', 'Safety plans', 'Emergency resources']
    },
    {
      id: 'appeals',
      title: 'Appeals & Reviews',
      icon: TrendingUpIcon,
      description: 'Appeal unfavorable court decisions',
      items: ['Appeal types', '30-day deadlines', 'Appeal forms']
    },
    {
      id: 'property',
      title: 'Property Division',
      icon: HomeIcon,
      description: 'Division of matrimonial property',
      items: ['Property valuation', 'Equal division', 'Matrimonial home']
    }
  ];

  return (
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl font-bold text-center text-gray-800 mb-12">Family Law Topics</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {topics.map((topic) => {
            const IconComponent = topic.icon;
            return (
              <div
                key={topic.id}
                onClick={() => topic.id === 'appeals' ? setCurrentView('appeals') : null}
                className="bg-gray-50 p-6 rounded-lg hover:shadow-lg transition-shadow cursor-pointer border border-gray-200"
              >
                <div className="flex items-center mb-4">
                  <div className="p-3 bg-blue-100 rounded-lg mr-4">
                    <IconComponent className="w-6 h-6 text-blue-900" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800">{topic.title}</h3>
                </div>
                
                <p className="text-gray-600 mb-4">{topic.description}</p>
                
                <ul className="space-y-2">
                  {topic.items.map((item, index) => (
                    <li key={index} className="text-sm text-blue-600 hover:text-blue-800 cursor-pointer">
                      • {item}
                    </li>
                  ))}
                </ul>
                
                <div className="mt-4 text-sm text-orange-600 font-medium">
                  See all →
                </div>
              </div>
            );
          })}
        </div>
        
        <div className="text-center mt-12">
          <button className="bg-blue-900 text-white px-8 py-3 rounded-full hover:bg-blue-800 transition-colors font-medium">
            Show all legal topics
          </button>
        </div>
      </div>
    </section>
  );
};

// Smart Solutions Component
export const SmartSolutions = () => {
  const pathways = [
    { title: 'Divorce and Separation', icon: HeartIcon },
    { title: 'Child Custody', icon: UsersIcon },
    { title: 'Child Support', icon: CurrencyDollarIcon },
    { title: 'Spousal Support', icon: HandshakeIcon },
    { title: 'Property Division', icon: HomeIcon },
    { title: 'Domestic Violence', icon: ShieldCheckIcon },
    { title: 'Appeals Process', icon: TrendingUpIcon },
    { title: 'Court Preparation', icon: ScaleIcon }
  ];

  return (
    <section className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <div>
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">
              <span className="text-orange-500">Smart Solutions</span>
            </h2>
            <h3 className="text-2xl font-semibold text-gray-800 mb-6">
              Guided Family Law Information
            </h3>
            
            <p className="text-gray-600 mb-6">
              Browse guided pathways, official forms, reviewed resources, and practical court-preparation information.
            </p>
            
            <div className="grid grid-cols-2 gap-4 mb-6">
              {pathways.map((pathway, index) => {
                const IconComponent = pathway.icon;
                return (
                  <button
                    key={index}
                    className="flex items-center space-x-2 p-3 bg-white rounded border border-gray-200 hover:border-orange-500 hover:shadow-md transition-all text-left"
                  >
                    <div className="p-2 bg-orange-100 rounded">
                      <IconComponent className="w-4 h-4 text-orange-600" />
                    </div>
                    <span className="text-sm font-medium text-gray-700">{pathway.title}</span>
                  </button>
                );
              })}
            </div>
            
            <button className="bg-orange-500 text-white px-6 py-3 rounded-full hover:bg-orange-600 transition-colors font-medium">
              Learn more about Smart Solutions
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

// Questions Section Component
export const QuestionsSection = () => {
  const newestQuestions = [
    "Can I get legal aid for a family law case in Ontario?",
    "Am I eligible for the Canadian Child Care Benefit after divorce?",
    "How do I complete a motion for interim custody?",
    "I have a court date but my landlord wants to evict me. What can I do?",
    "My ex-partner won't follow the custody order. What can I do?"
  ];

  const mostVisitedQuestions = [
    "Can I get EI if I quit my job due to domestic violence?",
    "I lost in Small Claims Court, but can't afford to pay. What can I do?",
    "Can I be evicted if my landlord wants to move in?",
    "How do we get married in Ontario?",
    "Does my employer have to give me a schedule of minimum hours of work?"
  ];

  return (
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
              <span className="text-orange-500 mr-2">📋</span>
              Newest Questions
            </h2>
            
            <div className="space-y-4">
              {newestQuestions.map((question, index) => (
                <div key={index} className="p-4 border border-gray-200 rounded hover:border-orange-500 hover:shadow-md transition-all cursor-pointer">
                  <p className="text-gray-700 hover:text-blue-600">{question}</p>
                  <span className="text-sm text-orange-600 font-medium">Learn more →</span>
                </div>
              ))}
            </div>
          </div>
          
          <div>
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
              <span className="text-orange-500 mr-2">⭐</span>
              Most Visited Questions
            </h2>
            
            <div className="space-y-4">
              {mostVisitedQuestions.map((question, index) => (
                <div key={index} className="p-4 border border-gray-200 rounded hover:border-orange-500 hover:shadow-md transition-all cursor-pointer">
                  <p className="text-gray-700 hover:text-blue-600">{question}</p>
                  <span className="text-sm text-orange-600 font-medium">Learn more →</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Forms Library Component
export const FormsLibrary = ({ user, onFormSelect }) => {
  const formCategories = [
    {
      category: 'Divorce Forms',
      forms: [
        { number: '8', name: 'Application (Divorce)', description: 'Start a divorce case' },
        { number: '36', name: 'Affidavit for Divorce', description: 'Support your divorce application' },
        { number: '25A', name: 'Divorce Order', description: 'Final divorce order template' }
      ]
    },
    {
      category: 'Custody & Access Forms',
      forms: [
        { number: '35.1', name: 'Affidavit in Support of Claim for Custody or Access', description: 'Evidence for custody claims' },
        { number: '14', name: 'Notice of Motion', description: 'Request court orders' },
        { number: '14A', name: 'Affidavit (General)', description: 'General sworn statement' }
      ]
    },
    {
      category: 'Support Forms',
      forms: [
        { number: '13', name: 'Financial Statement (Support Claims)', description: 'Income and expense details for support' },
        { number: '13.1', name: 'Financial Statement (Property and Support Claims)', description: 'Comprehensive financial disclosure' },
        { number: '26', name: 'Statement of Money Owed', description: 'Support arrears calculation' }
      ]
    },
    {
      category: 'Appeal Forms',
      forms: [
        { number: '61A', name: 'Notice of Appeal', description: 'Start an appeal process' },
        { number: '61B', name: 'Appellant\'s Certificate', description: 'Certify appeal documents' },
        { number: '61C', name: 'Appeal Book and Compendium', description: 'Appeal documentation' }
      ]
    }
  ];

  return (
    <div className="py-16 bg-gray-50 min-h-screen">
      <div className="container mx-auto px-4">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">Ontario Family Court Forms</h1>
          <p className="text-lg text-gray-600">
            Browse family court form information and review the current official form before use.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {formCategories.map((category, index) => (
            <div key={index} className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-2xl font-bold text-blue-900 mb-6">{category.category}</h2>
              
              <div className="space-y-4">
                {category.forms.map((form, formIndex) => (
                  <div key={formIndex} className="border border-gray-200 rounded p-4 hover:border-orange-500 hover:shadow-md transition-all cursor-pointer">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-semibold text-gray-800">Form {form.number}: {form.name}</h3>
                      <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded text-xs font-medium">
                        Fillable
                      </span>
                    </div>
                    <p className="text-gray-600 text-sm mb-3">{form.description}</p>
                    <div className="flex space-x-3">
                      <button 
                        onClick={() => onFormSelect && onFormSelect('new')}
                        className="bg-blue-900 text-white px-4 py-2 rounded text-sm hover:bg-blue-800 transition-colors"
                      >
                        Review Form
                      </button>
                      <button className="border border-gray-300 text-gray-700 px-4 py-2 rounded text-sm hover:border-gray-400 transition-colors">
                        Download PDF
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 bg-blue-900 text-white rounded-lg p-8">
          <h2 className="text-2xl font-bold mb-4">Need Help Choosing the Right Form?</h2>
          <p className="text-blue-100 mb-6">
            Use the current forms library and official court resources to review available forms.
          </p>
        </div>
      </div>
    </div>
  );
};

// Retained temporarily for source-history reference only. App.js routes to the
// owner-scoped Phase 5C Calendar component instead.
export const LegacyUnsafeCalendar = ({ user }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    event_type: 'court_hearing',
    priority: 'medium',
    start_date: '',
    end_date: '',
    location: ''
  });

  useEffect(() => {
    if (user) {
      loadEvents();
    }
  }, [user]);

  const loadEvents = async () => {
    setLoading(true);
    try {
      const response = await calendarAPI.getEvents();
      setEvents(response.data);
    } catch (error) {
      console.error('Error loading events:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddEvent = async (e) => {
    e.preventDefault();
    try {
      await calendarAPI.createEvent(newEvent);
      setNewEvent({
        title: '',
        description: '',
        event_type: 'court_hearing',
        priority: 'medium',
        start_date: '',
        end_date: '',
        location: ''
      });
      setShowAddEvent(false);
      loadEvents();
    } catch (error) {
      console.error('Error creating event:', error);
      alert('Error creating event. Please try again.');
    }
  };

  const getEventTypeColor = (type) => {
    switch(type) {
      case 'court_hearing': return 'bg-red-100 text-red-800 border-red-200';
      case 'deadline': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'appeal_deadline': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'meeting': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'reminder': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getPriorityIcon = (priority) => {
    switch(priority) {
      case 'critical': return '🚨';
      case 'high': return '⚠️';
      case 'medium': return '📋';
      case 'low': return '📝';
      default: return '📋';
    }
  };

  const formatEventType = (type) => {
    return type.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const upcomingEvents = events
    .filter(event => new Date(event.start_date) >= new Date())
    .sort((a, b) => new Date(a.start_date) - new Date(b.start_date))
    .slice(0, 10);

  const criticalEvents = events.filter(event => 
    event.priority === 'critical' && new Date(event.start_date) >= new Date()
  );

  if (!user) {
    return (
      <div className="py-16 bg-gray-50 min-h-screen">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-800 mb-4">Family Law Calendar</h1>
            <p className="text-lg text-gray-600 mb-8">
              Please log in to access your personalized calendar
            </p>
            <button 
              onClick={() => window.location.reload()}
              className="bg-blue-900 text-white px-8 py-3 rounded-full hover:bg-blue-800 transition-colors"
            >
              Login to Continue
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-16 bg-gray-50 min-h-screen">
      <div className="container mx-auto px-4">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">Family Law Calendar</h1>
          <p className="text-lg text-gray-600">
            Keep track of court dates, deadlines, and important family law milestones.
          </p>
        </div>

        {/* Critical Deadlines Alert */}
        {criticalEvents.length > 0 && (
          <div className="mb-8">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <h2 className="text-xl font-bold text-red-800 mb-4 flex items-center">
                <span className="text-2xl mr-2">🚨</span>
                Critical Deadlines Approaching
              </h2>
              <div className="space-y-3">
                {criticalEvents.map((event) => (
                  <div key={event.id} className="bg-white p-4 rounded border border-red-200">
                    <h3 className="font-semibold text-red-800">{event.title}</h3>
                    <p className="text-red-600 text-sm">
                      Due: {new Date(event.start_date).toLocaleDateString()} at {new Date(event.start_date).toLocaleTimeString()}
                    </p>
                    <p className="text-red-700 text-sm">{event.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-800">Upcoming Events</h2>
                <button
                  onClick={() => setShowAddEvent(true)}
                  className="bg-blue-900 text-white px-4 py-2 rounded hover:bg-blue-800 transition-colors"
                >
                  + Add Event
                </button>
              </div>
              
              {loading ? (
                <div className="text-center py-8">
                  <div className="w-8 h-8 border-4 border-blue-900 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading events...</p>
                </div>
              ) : upcomingEvents.length > 0 ? (
                <div className="space-y-4">
                  {upcomingEvents.map((event) => (
                    <div key={event.id} className={`border-l-4 border-orange-500 bg-white p-4 rounded-r shadow-sm ${getEventTypeColor(event.event_type)}`}>
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="font-semibold flex items-center">
                          <span className="mr-2">{getPriorityIcon(event.priority)}</span>
                          {event.title}
                        </h3>
                        <span className="text-xs font-medium px-2 py-1 rounded uppercase">
                          {formatEventType(event.event_type)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-1">
                        📅 {new Date(event.start_date).toLocaleDateString()} at {new Date(event.start_date).toLocaleTimeString()}
                      </p>
                      {event.location && (
                        <p className="text-sm text-gray-600 mb-1">
                          📍 {event.location}
                        </p>
                      )}
                      {event.description && (
                        <p className="text-sm text-gray-600 mb-3">
                          {event.description}
                        </p>
                      )}
                      <div className="flex space-x-2">
                        <button className="text-xs bg-blue-900 text-white px-3 py-1 rounded hover:bg-blue-800 transition-colors">
                          Edit
                        </button>
                        <button className="text-xs border border-gray-300 text-gray-700 px-3 py-1 rounded hover:border-gray-400 transition-colors">
                          Details
                        </button>
                        <button className="text-xs border border-gray-300 text-gray-700 px-3 py-1 rounded hover:border-gray-400 transition-colors">
                          Remind Me
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-600 mb-4">No upcoming events scheduled.</p>
                  <button
                    onClick={() => setShowAddEvent(true)}
                    className="bg-orange-500 text-white px-6 py-3 rounded-full hover:bg-orange-600 transition-colors"
                  >
                    Add Your First Event
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Critical Deadlines</h3>
              <div className="space-y-3">
                <div className="bg-red-50 border border-red-200 rounded p-3">
                  <h4 className="font-semibold text-red-800">Appeal Deadlines</h4>
                  <p className="text-sm text-red-600">
                    Time rules vary; review the current rule and its exact condition
                  </p>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                  <h4 className="font-semibold text-yellow-800">Support Payments</h4>
                  <p className="text-sm text-yellow-600">Monthly on the 1st</p>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded p-3">
                  <h4 className="font-semibold text-blue-800">Court Appearances</h4>
                  <p className="text-sm text-blue-600">Arrive 30 minutes early</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Quick Actions</h3>
              <div className="space-y-2">
                <button 
                  onClick={() => setShowAddEvent(true)}
                  className="w-full text-left p-3 border border-gray-200 rounded hover:border-orange-500 hover:shadow-md transition-all"
                >
                  📄 Add court date
                </button>
                <button className="w-full text-left p-3 border border-gray-200 rounded hover:border-orange-500 hover:shadow-md transition-all">
                  ⏰ Set deadline reminder
                </button>
                <button className="w-full text-left p-3 border border-gray-200 rounded hover:border-orange-500 hover:shadow-md transition-all">
                  📧 Export calendar
                </button>
                <button className="w-full text-left p-3 border border-gray-200 rounded hover:border-orange-500 hover:shadow-md transition-all">
                  🔔 Notification settings
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Add Event Modal */}
        {showAddEvent && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Add New Event</h2>
              
              <form onSubmit={handleAddEvent} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title *
                  </label>
                  <input
                    type="text"
                    required
                    value={newEvent.title}
                    onChange={(e) => setNewEvent({...newEvent, title: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Event Type
                  </label>
                  <select
                    value={newEvent.event_type}
                    onChange={(e) => setNewEvent({...newEvent, event_type: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="court_hearing">Court Hearing</option>
                    <option value="deadline">Deadline</option>
                    <option value="appeal_deadline">Appeal Deadline</option>
                    <option value="meeting">Meeting</option>
                    <option value="reminder">Reminder</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Priority
                  </label>
                  <select
                    value={newEvent.priority}
                    onChange={(e) => setNewEvent({...newEvent, priority: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date & Time *
                  </label>
                  <input
                    type="datetime-local"
                    required
                    value={newEvent.start_date}
                    onChange={(e) => setNewEvent({...newEvent, start_date: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Location
                  </label>
                  <input
                    type="text"
                    value={newEvent.location}
                    onChange={(e) => setNewEvent({...newEvent, location: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    rows="3"
                    value={newEvent.description}
                    onChange={(e) => setNewEvent({...newEvent, description: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="flex space-x-3">
                  <button
                    type="submit"
                    className="flex-1 bg-blue-900 text-white py-2 rounded hover:bg-blue-800 transition-colors"
                  >
                    Add Event
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowAddEvent(false)}
                    className="flex-1 bg-gray-500 text-white py-2 rounded hover:bg-gray-600 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Resource Library Component
export const ResourceLibrary = () => {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchResults, setSearchResults] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadResources();
  }, []);

  const loadResources = async () => {
    setLoading(true);
    try {
      const response = await resourcesAPI.getAll(null, null, null, 50);
      setResources(response.data);
    } catch (error) {
      console.error('Error loading resources:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchResults = (results, query) => {
    setSearchResults(results);
    setSearchQuery(query);
  };

  const displayResources = searchResults !== null ? searchResults : resources;

  const getResourceIcon = (type) => {
    switch(type) {
      case 'case_law': return ScaleIcon;
      case 'legal_guide': return BookIcon;
      case 'form_template': return DocumentIcon;
      case 'video_tutorial': return VideoIcon;
      case 'article': return NewspaperIcon;
      default: return ClipboardListIcon;
    }
  };

  const formatResourceType = (type) => {
    return type.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const resourceCategories = [
    { title: 'Case Law Database', type: 'case_law', description: 'Searchable database of Ontario family law cases', icon: ScaleIcon },
    { title: 'Legal Guides', type: 'legal_guide', description: 'Comprehensive guides for self-represented parents', icon: BookIcon },
    { title: 'Sample Documents', type: 'form_template', description: 'Templates and examples of legal documents', icon: DocumentIcon },
    { title: 'Video Tutorials', type: 'video_tutorial', description: 'Educational videos and workshops', icon: VideoIcon },
    { title: 'Legal Articles', type: 'article', description: 'Latest articles and legal updates', icon: NewspaperIcon }
  ];

  return (
    <div className="py-16 bg-gray-50 min-h-screen">
      <div className="container mx-auto px-4">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">Family Law Resource Library</h1>
          <p className="text-lg text-gray-600">
            Comprehensive collection of family law resources, case law, and educational materials.
          </p>
        </div>

        {/* Search and Filters */}
        <div className="mb-8">
          <AdvancedSearch onResults={handleSearchResults} />
        </div>

        {/* Resource Categories Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {resourceCategories.map((category, index) => {
            const IconComponent = category.icon;
            return (
              <div key={index} className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow">
                <div className="flex items-center mb-4">
                  <div className="p-3 bg-blue-100 rounded-lg mr-3">
                    <IconComponent className="w-6 h-6 text-blue-900" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-800">{category.title}</h3>
                    <p className="text-gray-600 text-sm">{category.description}</p>
                  </div>
                </div>
                <button 
                  onClick={() => handleFilterChange('resourceType', category.type)}
                  className="w-full bg-gray-100 text-gray-700 py-2 rounded hover:bg-gray-200 transition-colors"
                >
                  Browse {category.title}
                </button>
              </div>
            );
          })}
        </div>

        {/* Resources List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-4 border-blue-900 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading resources...</p>
          </div>
        ) : (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-800">
              {searchResults !== null ? 'Search Results' : 'All Resources'} 
              ({displayResources.length} found)
            </h2>
            
            <div className="grid grid-cols-1 gap-6">
                {displayResources.map((resource) => (
                <div key={resource.id} className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow">
                  {resource.resource_type === 'video_tutorial' && resource.url ? (
                    <div className="mb-6">
                      <VideoPlayer 
                        videoUrl={resource.url}
                        title={resource.title}
                        description={resource.description}
                      />
                    </div>
                  ) : (
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <div className="p-2 bg-blue-100 rounded mr-3">
                            {(() => {
                              const IconComponent = getResourceIcon(resource.resource_type);
                              return <IconComponent className="w-5 h-5 text-blue-900" />;
                            })()}
                          </div>
                          <div>
                            <h3 className="text-xl font-bold text-gray-800">{resource.title}</h3>
                            <div className="flex items-center space-x-4 text-sm text-gray-600">
                              <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                {formatResourceType(resource.resource_type)}
                              </span>
                              <span>Category: {resource.category}</span>
                              {resource.author && <span>By: {resource.author}</span>}
                            </div>
                          </div>
                        </div>
                        
                        <p className="text-gray-600 mb-4">{resource.description}</p>
                        
                        {resource.tags && resource.tags.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-4">
                            {resource.tags.map((tag, index) => (
                              <span key={index} className="bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs">
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                        
                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                          <span>👁️ {resource.views} views</span>
                          {resource.rating > 0 && (
                            <span>⭐ {resource.rating.toFixed(1)} rating</span>
                          )}
                          {resource.date_published && (
                            <span>📅 {new Date(resource.date_published).toLocaleDateString()}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex space-x-3">
                    {resource.url && resource.resource_type !== 'video_tutorial' ? (
                      <a 
                        href={resource.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="bg-blue-900 text-white px-4 py-2 rounded hover:bg-blue-800 transition-colors"
                      >
                        View Resource
                      </a>
                    ) : !resource.url && resource.resource_type !== 'video_tutorial' ? (
                      <button className="bg-blue-900 text-white px-4 py-2 rounded hover:bg-blue-800 transition-colors">
                        Read More
                      </button>
                    ) : null}
                    <button className="border border-gray-300 text-gray-700 px-4 py-2 rounded hover:border-gray-400 transition-colors">
                      Save to Library
                    </button>
                    <button className="border border-gray-300 text-gray-700 px-4 py-2 rounded hover:border-gray-400 transition-colors">
                      Share
                    </button>
                  </div>
                </div>
              ))}
              
              {displayResources.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-gray-600 text-lg">No resources found matching your criteria.</p>
                  <button 
                    onClick={() => {
                      setSearchResults(null);
                      setSearchQuery('');
                    }}
                    className="mt-4 text-blue-600 hover:text-blue-800"
                  >
                    Clear filters to see all resources
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Featured Resource Section */}
        <div className="mt-12 bg-blue-900 text-white rounded-lg p-8">
          <h2 className="text-2xl font-bold mb-4">Featured Resource</h2>
          <div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Ontario Family Law Self-Help Guide 2024</h3>
              <p className="text-blue-100 mb-4">
                Comprehensive 200-page guide covering all aspects of family law for self-represented litigants. 
                Updated with the latest legal changes and court procedures.
              </p>
              <button className="bg-orange-500 text-white px-6 py-3 rounded-full hover:bg-orange-600 transition-colors font-medium">
                Download Free Guide
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Retained only for historical review. App.js uses Directory.jsx, and the
// database-backed /api/contacts feed this component called is retired.
export const LegacyUnverifiedDirectory = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    contactType: '',
    region: '',
    emergencyOnly: false
  });

  useEffect(() => {
    loadContacts();
  }, [filters]);

  const loadContacts = async () => {
    setLoading(true);
    try {
      const response = await contactsAPI.getAll(
        filters.contactType || null,
        filters.region || null,
        filters.emergencyOnly
      );
      setContacts(response.data);
    } catch (error) {
      console.error('Error loading contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const formatContactType = (type) => {
    return type.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const getContactIcon = (type) => {
    switch(type) {
      case 'court': return '🏛️';
      case 'legal_aid': return '🏢';
      case 'support_service': return '🤝';
      case 'government': return '🏛️';
      case 'emergency': return '🚨';
      default: return '📞';
    }
  };

  const emergencyContacts = contacts.filter(contact => contact.emergency_contact);
  const regularContacts = contacts.filter(contact => !contact.emergency_contact);

  // Group contacts by type
  const contactsByType = regularContacts.reduce((acc, contact) => {
    const type = contact.contact_type;
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(contact);
    return acc;
  }, {});

  return (
    <div className="py-16 bg-gray-50 min-h-screen">
      <div className="container mx-auto px-4">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">Important Addresses & Phone Numbers</h1>
          <p className="text-lg text-gray-600">
            Essential contact information for family law services across Ontario.
          </p>
        </div>

        {/* Emergency Contacts */}
        {emergencyContacts.length > 0 && (
          <div className="mb-8">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <h2 className="text-xl font-bold text-red-800 mb-4 flex items-center">
                <span className="text-2xl mr-2">🚨</span>
                Emergency Contacts
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {emergencyContacts.map((contact) => (
                  <div key={contact.id} className="text-center">
                    <h3 className="font-semibold text-red-700">{contact.name}</h3>
                    <p className="text-2xl font-bold text-red-800">{contact.phone}</p>
                    <p className="text-sm text-red-600">{contact.hours}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="mb-8">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Filter Contacts</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <select 
                value={filters.contactType}
                onChange={(e) => handleFilterChange('contactType', e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Contact Types</option>
                <option value="court">Courts</option>
                <option value="legal_aid">Legal Aid</option>
                <option value="support_service">Support Services</option>
                <option value="government">Government</option>
              </select>
              
              <select 
                value={filters.region}
                onChange={(e) => handleFilterChange('region', e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Regions</option>
                <option value="Toronto">Toronto</option>
                <option value="Ottawa">Ottawa</option>
                <option value="Hamilton">Hamilton</option>
                <option value="Windsor">Windsor</option>
                <option value="Ontario">Province-wide</option>
              </select>
              
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={filters.emergencyOnly}
                  onChange={(e) => handleFilterChange('emergencyOnly', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-gray-700">Emergency contacts only</span>
              </label>
              
              <button 
                onClick={() => setFilters({ contactType: '', region: '', emergencyOnly: false })}
                className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 transition-colors"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-4 border-blue-900 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading contacts...</p>
          </div>
        ) : (
          <div className="space-y-8">
            {Object.entries(contactsByType).map(([type, typeContacts]) => (
              <div key={type} className="bg-white rounded-lg shadow-lg p-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                  <span className="text-3xl mr-3">{getContactIcon(type)}</span>
                  {formatContactType(type)} ({typeContacts.length})
                </h2>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                  {typeContacts.map((contact) => (
                    <div key={contact.id} className="border border-gray-200 rounded-lg p-4 hover:border-orange-500 hover:shadow-md transition-all">
                      <h3 className="font-semibold text-gray-800 mb-3">{contact.name}</h3>
                      
                      <div className="space-y-2 text-sm text-gray-600 mb-4">
                        {contact.address && (
                          <div className="flex items-start space-x-2">
                            <span>📍</span>
                            <span>{contact.address}</span>
                          </div>
                        )}
                        
                        {contact.phone && (
                          <div className="flex items-center space-x-2">
                            <span>📞</span>
                            <a href={`tel:${contact.phone}`} className="text-blue-600 hover:text-blue-800">
                              {contact.phone}
                            </a>
                          </div>
                        )}
                        
                        {contact.email && (
                          <div className="flex items-center space-x-2">
                            <span>📧</span>
                            <a href={`mailto:${contact.email}`} className="text-blue-600 hover:text-blue-800">
                              {contact.email}
                            </a>
                          </div>
                        )}
                        
                        {contact.website && (
                          <div className="flex items-center space-x-2">
                            <span>🌐</span>
                            <a href={contact.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                              Visit Website
                            </a>
                          </div>
                        )}
                        
                        {contact.hours && (
                          <div className="flex items-center space-x-2">
                            <span>🕒</span>
                            <span>{contact.hours}</span>
                          </div>
                        )}
                      </div>
                      
                      {contact.services && contact.services.length > 0 && (
                        <div className="mb-4">
                          <h4 className="font-medium text-gray-800 mb-2">Services:</h4>
                          <div className="flex flex-wrap gap-1">
                            {contact.services.slice(0, 3).map((service, index) => (
                              <span key={index} className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                                {service}
                              </span>
                            ))}
                            {contact.services.length > 3 && (
                              <span className="text-gray-500 text-xs">+{contact.services.length - 3} more</span>
                            )}
                          </div>
                        </div>
                      )}
                      
                      <div className="flex space-x-2">
                        {contact.address && (
                          <button className="text-xs bg-blue-900 text-white px-3 py-1 rounded hover:bg-blue-800 transition-colors">
                            Get Directions
                          </button>
                        )}
                        <button className="text-xs border border-gray-300 text-gray-700 px-3 py-1 rounded hover:border-gray-400 transition-colors">
                          Save Contact
                        </button>
                        <button className="text-xs border border-gray-300 text-gray-700 px-3 py-1 rounded hover:border-gray-400 transition-colors">
                          Share
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            
            {Object.keys(contactsByType).length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-600 text-lg">No contacts found matching your criteria.</p>
                <button 
                  onClick={() => setFilters({ contactType: '', region: '', emergencyOnly: false })}
                  className="mt-4 text-blue-600 hover:text-blue-800"
                >
                  Clear filters to see all contacts
                </button>
              </div>
            )}
          </div>
        )}

        <div className="mt-12 bg-blue-900 text-white rounded-lg p-8">
          <h2 className="text-2xl font-bold mb-4">Can't Find What You're Looking For?</h2>
          <p className="text-blue-100 mb-6">
            Adjust the directory filters or browse all entries to find relevant services and contact information.
          </p>
          <p className="text-blue-200 text-sm">For urgent safety concerns, use the emergency contacts listed above.</p>
        </div>
      </div>
    </div>
  );
};

// Court Guide Component
export const CourtGuide = () => {
  const courtTips = [
    {
      category: 'Professional Attire',
      icon: '👔',
      tips: [
        'Business casual or formal business attire',
        'Avoid shorts, tank tops, flip-flops, or revealing clothing',
        'Remove hats and sunglasses inside the courtroom',
        'Keep jewelry minimal and professional',
        'Ensure clothing is clean, pressed, and well-fitted'
      ]
    },
    {
      category: 'Courtroom Etiquette',
      icon: '⚖️',
      tips: [
        'Address the judge as "Your Honour"',
        'Stand when the judge enters or leaves',
        'Speak only when given permission',
        'Turn off all electronic devices',
        'Do not interrupt others when they are speaking'
      ]
    },
    {
      category: 'Before Your Hearing',
      icon: '📋',
      tips: [
        'Arrive 30 minutes early',
        'Bring all required documents in organized folders',
        'Review your case materials the night before',
        'Prepare key points you want to make',
        'Bring a notebook and pen for taking notes'
      ]
    },
    {
      category: 'During the Hearing',
      icon: '🗣️',
      tips: [
        'Speak clearly and at a moderate pace',
        'Stick to the facts and avoid emotional language',
        'Answer only the question asked',
        'Say "I don\'t know" if you don\'t know something',
        'Remain calm and respectful at all times'
      ]
    }
  ];

  const documentChecklist = [
    'Government-issued photo ID',
    'All court documents related to your case',
    'Financial documents (if discussing support)',
    'Any evidence you plan to present',
    'Contact information for all parties',
    'Notebook and pen',
    'Copy of relevant laws or cases (if applicable)',
    'Any expert reports or assessments'
  ];

  return (
    <div className="py-16 bg-gray-50 min-h-screen">
      <div className="container mx-auto px-4">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">Family Court Guide</h1>
          <p className="text-lg text-gray-600">
            Essential tips for courtroom etiquette, professional attire, and hearing preparation.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {courtTips.map((section, index) => (
            <div key={index} className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                <span className="text-3xl mr-3">{section.icon}</span>
                {section.category}
              </h2>
              
              <ul className="space-y-3">
                {section.tips.map((tip, tipIndex) => (
                  <li key={tipIndex} className="flex items-start space-x-3">
                    <span className="text-green-500 mt-0.5">✓</span>
                    <span className="text-gray-700">{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Document Checklist</h2>
            <p className="text-gray-600 mb-4">
              Make sure you have all necessary documents before your court appearance:
            </p>
            
            <div className="space-y-3">
              {documentChecklist.map((item, index) => (
                <label key={index} className="flex items-center space-x-3 cursor-pointer">
                  <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                  <span className="text-gray-700">{item}</span>
                </label>
              ))}
            </div>
            
            <button className="mt-6 bg-blue-900 text-white px-6 py-3 rounded-full hover:bg-blue-800 transition-colors font-medium">
              Print Checklist
            </button>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Court Locations</h2>
            <p className="text-gray-600 mb-4">
              Family courts are located throughout Ontario. Find the court handling your case:
            </p>
            
            <div className="space-y-4">
              <div className="border border-gray-200 rounded p-4">
                <h3 className="font-semibold text-gray-800">Superior Court of Justice</h3>
                <p className="text-gray-600 text-sm">Handles divorce, custody, support, and property matters</p>
              </div>
              
              <div className="border border-gray-200 rounded p-4">
                <h3 className="font-semibold text-gray-800">Ontario Court of Justice</h3>
                <p className="text-gray-600 text-sm">Handles custody, access, support, and child protection</p>
              </div>
              
              <div className="border border-gray-200 rounded p-4">
                <h3 className="font-semibold text-gray-800">Unified Family Court</h3>
                <p className="text-gray-600 text-sm">Available in select locations - handles all family matters</p>
              </div>
            </div>
            
            <button className="mt-4 bg-orange-500 text-white px-6 py-3 rounded-full hover:bg-orange-600 transition-colors font-medium">
              Find Your Court
            </button>
          </div>
        </div>

        <div className="bg-blue-900 text-white rounded-lg p-8">
          <h2 className="text-2xl font-bold mb-4">What to Expect in Family Court</h2>
          <div>
            <div>
              <p className="text-blue-100 mb-6">
                Family court can be intimidating, but being prepared helps. The judge's role is to make decisions 
                based on the best interests of children and the law. Stay calm, be respectful, and focus on facts.
              </p>
              <ul className="space-y-2 text-blue-100">
                <li>• Hearings can last 30 minutes to several hours</li>
                <li>• You may need to return for multiple appearances</li>
                <li>• The judge may ask you direct questions</li>
                <li>• Decisions may be made immediately or reserved</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Appeals Component
export const Appeals = () => {
  const appealTypes = [
    {
      type: 'Appeal as of Right',
      description: 'Automatic right to appeal certain final orders',
      timeline: '30 days from the date of the order',
      cost: '$205 filing fee',
      examples: ['Final divorce orders', 'Final custody orders', 'Final support orders']
    },
    {
      type: 'Leave to Appeal',
      description: 'Must get permission from the court to appeal',
      timeline: '15 days to file motion for leave',
      cost: '$125 for motion + $205 if leave granted',
      examples: ['Interim orders', 'Case management orders', 'Procedural decisions']
    }
  ];

  const appealProcess = [
    {
      step: 1,
      title: 'Determine Appeal Rights',
      description: 'Check if you have an automatic right to appeal or need permission',
      timeframe: 'Immediately after judgment'
    },
    {
      step: 2,
      title: 'File Notice of Appeal',
      description: 'File Form 61A within the deadline',
      timeframe: '30 days (or 15 days for leave)'
    },
    {
      step: 3,
      title: 'Serve Other Parties',
      description: 'Serve all parties with the notice of appeal',
      timeframe: 'Within 10 days of filing'
    },
    {
      step: 4,
      title: 'Prepare Appeal Materials',
      description: 'Gather transcripts, evidence, and legal arguments',
      timeframe: '30-60 days'
    },
    {
      step: 5,
      title: 'File Appeal Book',
      description: 'Submit organized case materials to appeal court',
      timeframe: 'As directed by court'
    },
    {
      step: 6,
      title: 'Appeal Hearing',
      description: 'Present arguments to panel of appeal judges',
      timeframe: '6-12 months after filing'
    }
  ];

  return (
    <div className="py-16 bg-gray-50 min-h-screen">
      <div className="container mx-auto px-4">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">Family Law Appeals Process</h1>
          <p className="text-lg text-gray-600">
            Understanding your rights and deadlines for appealing family court decisions in Ontario.
          </p>
        </div>

        <div className="mb-12 bg-red-50 border border-red-200 rounded-lg p-6">
          <h2 className="text-2xl font-bold text-red-800 mb-4">⚠️ Critical Appeal Deadlines</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-red-700 mb-2">Appeals as of Right</h3>
              <p className="text-red-600">30 days from the date of the order</p>
            </div>
            <div>
              <h3 className="font-semibold text-red-700 mb-2">Leave to Appeal Applications</h3>
              <p className="text-red-600">15 days from the date of the order</p>
            </div>
          </div>
          <p className="text-red-700 font-medium mt-4">
            Missing these deadlines means you lose your right to appeal forever!
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {appealTypes.map((appeal, index) => (
            <div key={index} className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">{appeal.type}</h2>
              
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-gray-800">Description</h3>
                  <p className="text-gray-600">{appeal.description}</p>
                </div>
                
                <div>
                  <h3 className="font-semibold text-gray-800">Timeline</h3>
                  <p className="text-red-600 font-medium">{appeal.timeline}</p>
                </div>
                
                <div>
                  <h3 className="font-semibold text-gray-800">Cost</h3>
                  <p className="text-gray-600">{appeal.cost}</p>
                </div>
                
                <div>
                  <h3 className="font-semibold text-gray-800">Examples</h3>
                  <ul className="text-gray-600 space-y-1">
                    {appeal.examples.map((example, exampleIndex) => (
                      <li key={exampleIndex}>• {example}</li>
                    ))}
                  </ul>
                </div>
              </div>
              
              <button className="mt-6 w-full bg-blue-900 text-white py-3 rounded-lg hover:bg-blue-800 transition-colors font-medium">
                Start {appeal.type} Process
              </button>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6 mb-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Appeal Process Steps</h2>
          
          <div className="space-y-6">
            {appealProcess.map((step, index) => (
              <div key={index} className="flex items-start space-x-4">
                <div className="bg-blue-900 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">
                  {step.step}
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-800 mb-1">{step.title}</h3>
                  <p className="text-gray-600 mb-2">{step.description}</p>
                  <span className="text-sm text-orange-600 font-medium">⏰ {step.timeframe}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Required Appeal Forms</h2>
            
            <div className="space-y-4">
              <div className="border border-gray-200 rounded p-4">
                <h3 className="font-semibold text-gray-800">Form 61A - Notice of Appeal</h3>
                <p className="text-gray-600 text-sm">Required for all appeals</p>
                <button className="mt-2 bg-orange-500 text-white px-4 py-2 rounded text-sm hover:bg-orange-600 transition-colors">
                  Review Form Information
                </button>
              </div>
              
              <div className="border border-gray-200 rounded p-4">
                <h3 className="font-semibold text-gray-800">Form 61B - Appellant's Certificate</h3>
                <p className="text-gray-600 text-sm">Certifies appeal record is complete</p>
                <button className="mt-2 bg-orange-500 text-white px-4 py-2 rounded text-sm hover:bg-orange-600 transition-colors">
                  Review Form Information
                </button>
              </div>
              
              <div className="border border-gray-200 rounded p-4">
                <h3 className="font-semibold text-gray-800">Motion for Leave (if required)</h3>
                <p className="text-gray-600 text-sm">For appeals requiring permission</p>
                <button className="mt-2 bg-orange-500 text-white px-4 py-2 rounded text-sm hover:bg-orange-600 transition-colors">
                  Review Form Information
                </button>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Appeal Success Factors</h2>
            
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-gray-800">Strong Grounds for Appeal</h3>
                <ul className="text-gray-600 text-sm space-y-1 mt-2">
                  <li>• Legal errors by the trial judge</li>
                  <li>• Misapplication of legal principles</li>
                  <li>• Failure to consider relevant evidence</li>
                  <li>• Procedural unfairness</li>
                </ul>
              </div>
              
              <div>
                <h3 className="font-semibold text-gray-800">Weak Grounds for Appeal</h3>
                <ul className="text-gray-600 text-sm space-y-1 mt-2">
                  <li>• Disagreeing with judge's decision</li>
                  <li>• New evidence not presented at trial</li>
                  <li>• Different interpretation of facts</li>
                  <li>• Hoping for a better outcome</li>
                </ul>
              </div>
              
              <p className="text-sm text-gray-600">
                Get legal advice about how the law applies to a specific order before deciding whether to appeal.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-blue-900 text-white rounded-lg p-8">
          <h2 className="text-2xl font-bold mb-4">Need Help with Your Appeal?</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <p className="text-blue-100 mb-6">
                Appeals are complex legal proceedings with strict deadlines. Review the current official
                court guidance and seek legal advice about your specific order where possible.
              </p>
              <div className="space-y-2 text-blue-100">
                <p>✓ Official court guidance</p>
                <p>✓ Current forms and rules</p>
                <p>✓ Legal-help directory</p>
              </div>
            </div>
            <div className="text-center">
              <button className="bg-orange-500 text-white px-8 py-4 rounded-full text-lg font-medium hover:bg-orange-600 transition-colors">
                Review Appeal Resources
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Footer Component
export const Footer = ({ setCurrentView }) => {
  const partnerLogos = [
    { name: 'Legal Aid Ontario', url: '#' },
    { name: 'Ontario Courts', url: '#' },
    { name: 'Family Mediation Canada', url: '#' },
    { name: 'CLEO', url: '#' },
    { name: 'Government of Ontario', url: '#' }
  ];

  const footerLinks = {
    'About Good Parents\' Justice': [
      'Our Mission',
      'Guided Pathways',
      'Legal Partners',
      'Glossary'
    ],
    'Latest Updates': [
      'News & Alerts',
      'Legal Updates',
      'New Court Forms',
      'Subscribe to CLEO updates'
    ],
    'Terms and Conditions': [
      'Privacy Policy',
      'Terms of Use',
      'Contact Us',
      'Disclaimer'
    ]
  };

  return (
    <footer className="bg-blue-900 text-white">
      <div className="container mx-auto px-4 py-12">
        <div className="mb-8">
          <h2 className="text-xl font-bold mb-4">Good Parents Justice Partners</h2>
          <div className="flex flex-wrap items-center space-x-8 space-y-4">
            {partnerLogos.map((partner, index) => (
              <div key={index} className="bg-white rounded p-3">
                <span className="text-blue-900 font-medium text-sm">{partner.name}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
          {Object.entries(footerLinks).map(([category, links]) => (
            <div key={category}>
              <h3 className="font-bold mb-4">{category}</h3>
              <ul className="space-y-2">
                {links.map((link, index) => (
                  <li key={index}>
                    <a href="#" className="text-blue-200 hover:text-white transition-colors text-sm">
                      {link}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="border-t border-blue-800 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-orange-500 rounded flex items-center justify-center font-bold text-xl">
                GPJ
              </div>
              <div>
                <h3 className="font-bold">Good Parents Justice</h3>
                <p className="text-blue-200 text-sm">Empowering Ontario families with legal knowledge</p>
              </div>
            </div>
            
            <div className="text-center md:text-right">
              <p className="text-blue-200 text-sm mb-2">
                This site contains general legal information for Ontario, Canada. It is not intended to 
                be used as legal advice for a specific legal problem.
              </p>
              <p className="text-blue-300 text-xs">
                Good Parents Justice - Independent legal information platform
                <br />for Ontario families navigating family court
              </p>
            </div>
          </div>
        </div>

        <div className="mt-8 text-center">
          <div className="flex justify-center space-x-4 text-blue-200 text-sm">
            <a href="#" className="hover:text-white transition-colors">© CLEO</a>
            <span>|</span>
            <a href="#" className="hover:text-white transition-colors">🌐 Steps to Justice</a>
            <span>|</span>
            <a href="#" className="hover:text-white transition-colors">👥 Guided Pathways</a>
            <span>|</span>
            <a href="#" className="hover:text-white transition-colors">📧 CLEO Connect</a>
          </div>
        </div>
      </div>
    </footer>
  );
};
